import { useState } from "react";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Gavel } from "lucide-react";

export default function AuctionRegisterPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", dealer_license: "", company: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post("/auction-registrations", form); setSubmitted(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  if (submitted) return (
    <PublicShell><section className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
      <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "var(--wakzy-gold)" }} />
      <h1 className="font-heading text-4xl mb-3 chrome-text inline-block">Application submitted.</h1>
      <p className="text-slate-300">We'll verify your details and email you bidding credentials within one business day.</p>
    </section></PublicShell>
  );
  return (
    <PublicShell>
      <section className="hero-bg pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow" style={{ color: "var(--wakzy-red)" }}>Register to bid</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-3"><span className="chrome-text">Get bidder</span> <span className="red-text">credentials.</span></h1>
          <p className="text-slate-300">Open to retail buyers and licensed dealers. Dealer license number is optional but unlocks dealer-only inventory.</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <form onSubmit={submit} className="surface p-8 rounded-md space-y-4" data-testid="auction-register-form">
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { k: "full_name", label: "Full name", req: true },
              { k: "email", label: "Email", type: "email", req: true },
              { k: "phone", label: "Phone" },
              { k: "company", label: "Company (optional)" },
              { k: "dealer_license", label: "Dealer license # (optional)" },
            ].map((f) => (
              <div key={f.k} className={f.k === "dealer_license" ? "md:col-span-2" : ""}>
                <Label className="text-sm text-slate-300">{f.label}{f.req && <span className="text-[#E63946]"> *</span>}</Label>
                <Input type={f.type || "text"} required={f.req} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="bg-[#04060d] border-white/10 mt-1 h-11" data-testid={`reg-${f.k}-input`} />
              </div>
            ))}
            <div className="md:col-span-2"><Label className="text-sm text-slate-300">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#04060d] border-white/10 mt-1" /></div>
          </div>
          <Button type="submit" disabled={submitting} className="btn-cta rounded-sm h-12 px-8" data-testid="reg-submit-btn">
            <Gavel className="w-4 h-4 mr-2" /> {submitting ? "Submitting…" : "Apply for Bidder Status"}
          </Button>
        </form>
      </section>
    </PublicShell>
  );
}
